// BM-H4482 (Vanessa, 19 Oct 2026): the Late Payment Agent as a standalone Bonsai Pen tool which
// can be linked to a matter. Lists your letters, starts a new one, and opens one in the editor.
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import LatePaymentPanel from "../components/LatePaymentPanel";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

const STATE = { intake: "Draft details", handed_back: "Stopped", awaiting_approval: "Waiting for approval",
                returned: "Sent back", approved: "Approved", sent: "Sent" };

export default function LatePaymentLetters({ apiBase = API }) {
  const { workupId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/late-payment`);
      setRows(res.data?.workups || []);
      setErr("");
    } catch (e) {
      setErr(errorMessage(e, "Could not list your letters."));
    }
  }, [apiBase]);

  useEffect(() => { if (!workupId) load(); }, [load, workupId]);

  const start = async () => {
    try {
      const res = await axios.post(`${apiBase}/late-payment`, {});
      navigate(`/bonsai-pen/late-payment/${res.data.workup.id}`);
    } catch (e) {
      toast.error(errorMessage(e, "Could not start a letter."));
    }
  };

  if (workupId) {
    return (
      <div className="mx-auto max-w-5xl p-6" data-testid="late-payment-letter-page">
        <Link to="/bonsai-pen/late-payment" className="text-xs underline text-txt-muted">All letters before claim</Link>
        <LatePaymentPanel workupId={workupId} apiBase={apiBase} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-6" data-testid="late-payment-letters">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-semibold text-txt">Letter before claim: late payment</h1>
        <Button size="sm" onClick={start} data-testid="lpl-new">New letter</Button>
      </div>
      <p className="text-sm text-txt-muted mb-5">
        An unpaid business debt to a letter before claim a solicitor has approved. Link it to a matter and the reply-by and chase
        dates go in that matter's diary. Just need the figures? Use the{" "}
        <Link className="underline" to="/bonsai-pen/late-payment-calculator">interest calculator</Link>.
      </p>
      {err && <p className="text-xs text-status-red">{err}</p>}
      {rows && rows.length === 0 && <p className="text-xs text-txt-muted">You have no letters yet.</p>}
      {rows && rows.length > 0 && (
        <table className="w-full text-xs text-txt" data-testid="lpl-list">
          <thead><tr className="text-left text-txt-muted"><th>Debtor</th><th>Client</th><th>Matter</th><th>Stage</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.debtor || "(not named yet)"}</td><td>{r.creditor}</td><td>{r.matter_ref || "Not linked"}</td>
                <td>{STATE[r.state] || r.state}</td>
                <td><Link className="underline" to={`/bonsai-pen/late-payment/${r.id}`}>Open</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
